import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Document, Page } from 'react-pdf';
import { API_URL } from '../../../appConstants';
import { Box, CircularProgress, Typography, Container, Button } from '@mui/material';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import PdfEditorClinician from './PdfEditorClinician';
import showToast from '../../../components/Hot-Toast/showToast';

const MobileClinicianEditOnboardingDocument = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pdfData, setPdfData] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [scale, setScale] = useState(1);
    const [initialFields, setInitialFields] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [showEditor, setShowEditor] = useState(true); // Always show editor by default

    // Get userId and documentId from URL query parameters
    const documentId = searchParams.get('documentId') || searchParams.get('DOCUMENT_ID');
    const userId = searchParams.get('userId') || searchParams.get('USER_ID');

    const fetchPdfData = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(
                `${API_URL}fetchClinicianOnboardingDocumentByIdAndUserId?DOCUMENT_ID=${documentId}&USER_ID=${userId}`
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.STATUS && data.OUTPUT) {
                setPdfData(data.OUTPUT);
                
                // Set the PDF URL with timestamp to prevent caching
                const pdfFileUrl = data.OUTPUT.PDF_FILE || data.OUTPUT.PDF_FILE_NAME;
                if (pdfFileUrl) {
                    setPdfUrl(`${pdfFileUrl}?t=${Date.now()}`);
                } else {
                    throw new Error('No PDF file URL found in response');
                }
                
                // Set initial fields from API response if available
                if (data.OUTPUT.PDF_EDITOR_FIELDS) {
                    try {
                        let fields = Array.isArray(data.OUTPUT.PDF_EDITOR_FIELDS) 
                            ? [...data.OUTPUT.PDF_EDITOR_FIELDS] 
                            : JSON.parse(data.OUTPUT.PDF_EDITOR_FIELDS);
                        
                        // Check if we have field values to prefill
                        if (data.OUTPUT.PDF_FIELD_VALUES) {
                            const fieldValues = data.OUTPUT.PDF_FIELD_VALUES;
                            console.log('Processing PDF_FIELD_VALUES:', fieldValues);
                            
                            // Handle all possible formats of PDF_FIELD_VALUES:
                            // 1. Direct field-value mapping: { "fieldId1": "value1" }
                            // 2. Object with fields array: { fields: [{ id: "field1", value: "value1" }] }
                            // 3. Object with fieldValues: { fieldValues: { "field1": "value1" } }
                            
                            let valuesToUse = {};
                            
                            // Case 1: Direct field-value mapping (check for any known field ID patterns)
                            if (typeof fieldValues === 'object' && !Array.isArray(fieldValues)) {
                                // Check if it's a direct mapping by looking for known field patterns
                                const hasDirectMapping = Object.keys(fieldValues).some(key => 
                                    key.startsWith('new_') || 
                                    key === 'name' || 
                                    key === 'signature' ||
                                    key === 'email' ||
                                    key === 'date' ||
                                    key.endsWith('_value') // Common pattern for field values
                                );
                                
                                if (hasDirectMapping) {
                                    valuesToUse = { ...fieldValues };
                                }
                            }
                            
                            // Case 2: Has fields array
                            if (Array.isArray(fieldValues?.fields)) {
                                fieldValues.fields.forEach(field => {
                                    if (field?.id && field.value !== undefined) {
                                        valuesToUse[field.id] = field.value;
                                    } else if (field?.fieldId && field.value !== undefined) {
                                        valuesToUse[field.fieldId] = field.value;
                                    }
                                });
                            }
                            
                            // Case 3: Has fieldValues object
                            if (fieldValues?.fieldValues && typeof fieldValues.fieldValues === 'object') {
                                valuesToUse = { ...valuesToUse, ...fieldValues.fieldValues };
                            }
                            
                            // Update fields with values from valuesToUse
                            fields = fields.map(field => {
                                // Try to find a matching value in valuesToUse
                                const fieldId = field.id;
                                let fieldValue = '';
                                
                                // Check for direct match first
                                if (fieldId && valuesToUse[fieldId] !== undefined) {
                                    fieldValue = valuesToUse[fieldId];
                                }
                                // Check for common field names if direct match not found
                                else if (field.name && valuesToUse[field.name] !== undefined) {
                                    fieldValue = valuesToUse[field.name];
                                }
                                // Handle special field types
                                else if (field.type === 'Text') {
                                    // For text fields, try to find by partial name match
                                    const matchingKey = Object.keys(valuesToUse).find(key => 
                                        key.toLowerCase().includes(field.name?.toLowerCase()) ||
                                        field.name?.toLowerCase().includes(key.toLowerCase())
                                    );
                                    if (matchingKey) {
                                        fieldValue = valuesToUse[matchingKey];
                                    }
                                }
                                
                                // If we found a value, update the field
                                if (fieldValue !== '') {
                                    console.log(`Prefilling field ${fieldId || field.name} with value:`, fieldValue);
                                    return {
                                        ...field,
                                        value: fieldValue,
                                        // Preserve existing value if no new value is found
                                        ...(fieldValue === undefined && { value: field.value || '' })
                                    };
                                }
                                
                                return field;
                            });
                            
                        }
                        
                        setInitialFields(fields);
                    } catch (e) {
                        console.error('Error processing PDF fields:', e);
                        setInitialFields([]);
                    }
                }
            } else {
                throw new Error(data.MESSAGE || 'Failed to fetch PDF data');
            }
        } catch (err) {
            console.error('Error fetching PDF data:', err);
            setError(err.message || 'Failed to load document');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!documentId || !userId) {
            setError('Missing required parameters: DOCUMENT_ID and USER_ID');
            setIsLoading(false);
            return;
        }
        fetchPdfData();
    }, [documentId, userId]);

    useEffect(() => {
        // Reset pages when pdfUrl changes
        setNumPages(null);
    }, [pdfUrl]);

    const onDocumentLoadSuccess = ({ numPages }) => {
        console.log('PDF loaded successfully with', numPages, 'pages');
        setNumPages(numPages);
        setScale(1.0); // Set default scale for mobile viewing
    };

    const handleEditClick = () => {
        setShowEditor(true);
    };

    const handleSavePdf = async (modifiedPdfUrl, updatedFields) => {
        try {
            setIsSaving(true);
            
            // Prepare the data to match the expected API format
            const saveData = {
                DOCUMENT_ID: documentId,
                USER_ID: userId,
                PDF_FIELD_VALUES: JSON.stringify(updatedFields),
                // Include any other required fields that the API expects
                // These should match the fields expected by the addClinicanOnboardingDocument endpoint
            };
            
            
            // Make the API call using the same pattern as in ClinicianProfileOnboardingDocument
            const response = await fetch(`${API_URL}addClinicanOnboardingDocument`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(saveData)
            });
            
            const result = await response.json();
            
            if (result?.STATUS) {
                // Update the fields in state
                setInitialFields(updatedFields);
                showToast('success', result.MESSAGE || 'Document saved successfully!', 3000);
            }
        } catch (error) {
            console.error('Error saving PDF:', error);
            setError('Failed to save document: ' + (error.message || 'Unknown error occurred'));
            showToast('error', error.message, 3000);
        } finally {
            setIsSaving(false);
        }
    };



    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" className="py-10">
                <Typography variant="h6" className="text-center text-red-500">
                    {error}
                </Typography>
            </Container>
        );
    }
    
    return (
        <Container maxWidth={false} disableGutters className="py-6" sx={{ p: 0, width: '100%', maxWidth: '100%' }}>
            {pdfUrl ? (
                <Box sx={{ p: 0, height: 'calc(100vh - 120px)', width: '100%' }}>
                    <PdfEditorClinician
                        pdfFile={pdfUrl}
                        pdfName={pdfData?.DOCUMENT_NAME || 'document.pdf'}
                        onSave={handleSavePdf}
                        initialFields={initialFields}
                        onFieldsChange={setInitialFields}
                        isReadOnly={false}
                        showPreviewOnly={false}
                        documentId={documentId}
                        userId={userId}
                    />
                    {isSaving && (
                        <Box sx={{ 
                            position: 'fixed', 
                            top: 0, 
                            left: 0, 
                            right: 0, 
                            bottom: 0, 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            zIndex: 1300
                        }}>
                            <CircularProgress color="primary" />
                        </Box>
                    )}
                </Box>
            ) : (
                <Typography variant="h6" className="text-center text-gray-500">
                    No PDF document available
                </Typography>
            )}
        </Container>
    );
};

export default MobileClinicianEditOnboardingDocument;